import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Paper,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStones } from '../../redux/actions/stone/stoneAction';
import { fetchStocks } from '../../redux/actions/stock/stockAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import { fetchColors } from '../../redux/actions/color/colorAction';
import { fetchRawStockCodes } from '../../redux/actions/rawStockCode/rawStockCodeAction';
import { createRawStock, updateRawStock } from '../../redux/actions/rawStock/rawStockAction';
import { showSuccess, showError } from '../../utils/alertUtils';

const initialForm = {
  stockType: '',
  karatage: '',
  color: '',
  stoneType: '',
  size: '',
  weight: '',
  remarks: '',
  referenceNumber: '',
  stockCode: '',
  stockName: '',
  qty: '',
};

const RawStockForm = ({ selectedRawStock, onReset }) => {
  const dispatch = useDispatch();
  const { stones } = useSelector((state) => state.stone);
  const { stocks } = useSelector((state) => state.stock);
  const { purities } = useSelector((state) => state.purity);
  const { colors } = useSelector((state) => state.color);
  const { rawStockCodes } = useSelector((state) => state.rawStockCode);
  const { rawStocks } = useSelector((state) => state.rawStock);
  const [formData, setFormData] = useState(initialForm);
  const [selectedCode, setSelectedCode] = useState(null);

  useEffect(() => {
    dispatch(fetchStones());
    dispatch(fetchStocks());
    dispatch(fetchPurities());
    dispatch(fetchColors());
    if (!rawStockCodes || rawStockCodes.length === 0) {
      dispatch(fetchRawStockCodes());
    }
  }, [dispatch, rawStockCodes]);

  useEffect(() => {
    if (selectedRawStock) {
      setFormData({
        stockType: stocks.find(s => s.id === selectedRawStock.stock_type_id)?.name || '',
        karatage: purities.find(p => p.id === selectedRawStock.karatage)?.name || '',
        color: colors.find(c => c.id === selectedRawStock.color)?.name || '',
        stoneType: stones.find(s => s.id === selectedRawStock.stone_type_id)?.name || '',
        size: selectedRawStock.size || '',
        weight: selectedRawStock.weight || '',
        remarks: selectedRawStock.remarks || '',
        referenceNumber: selectedRawStock.reference_number || '',
        stockCode: selectedRawStock.stock_code || '',
        stockName: selectedRawStock.stock_name || '',
        qty: selectedRawStock.qty || '',
      });
      const codeObj = rawStockCodes.find(
        code => code.stock_code === selectedRawStock.stock_code
      );
      setSelectedCode(codeObj || null);
    } else {
      setFormData(initialForm);
      setSelectedCode(null);
    }
  }, [selectedRawStock, stocks, stones, purities, colors, rawStockCodes]);

  // When a stock code is selected, auto-fill and lock fields (but not when editing)
  useEffect(() => {
    if (selectedCode && !selectedRawStock) {
      setFormData((prev) => ({
        ...prev,
        stockType: stocks.find(s => s.id === selectedCode.stock_type_id)?.name || '',
        karatage: purities.find(p => p.id === selectedCode.karatage_id)?.name || '',
        color: colors.find(c => c.id === selectedCode.color_id)?.name || '',
        stoneType: stones.find(s => s.id === selectedCode.stone_type_id)?.name || '',
        stockCode: selectedCode.stock_code || '',
        stockName: selectedCode.stock_name || '',
      }));
    }
  }, [selectedCode, stocks, stones, purities, colors, selectedRawStock]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Reference Number uniqueness validation
    const duplicateRef = rawStocks.some(
      (r) =>
        r.reference_number === formData.referenceNumber &&
        (!selectedRawStock || r.id !== selectedRawStock.id)
    );
    if (duplicateRef) {
      showError('Validation Error', 'Reference Number already exists.');
      return;
    }
    try {
      const stockTypeId = stocks.find(s => s.name === formData.stockType)?.id;
      const stoneTypeId = formData.stockType.toLowerCase() === 'stone' 
                          ? stones.find(s => s.name === formData.stoneType)?.id
                          : null;
      const karatageId = formData.stockType.toLowerCase() === 'gold' 
                         ? purities.find(p => p.name === formData.karatage)?.id
                         : null;
      const colorId = formData.stockType.toLowerCase() === 'gold' 
                      ? colors.find(c => c.name === formData.color)?.id
                      : null;

      // Client-side validation for required foreign keys
      if (!stockTypeId) {
        showError('Validation Error', 'Please select a Stock Type.');
        return;
      }

      if (formData.stockType.toLowerCase() === 'stone' && !stoneTypeId) {
        showError('Validation Error', 'Please select a Stone Type.');
        return;
      }

      if (formData.stockType.toLowerCase() === 'gold' && !karatageId) {
        showError('Validation Error', 'Please select Karatage.');
        return;
      }

      if (formData.stockType.toLowerCase() === 'gold' && !colorId) {
        showError('Validation Error', 'Please select a Color.');
        return;
      }

      // New fields validation
      if (!formData.stockCode) {
        showError('Validation Error', 'Stock Code is required.');
        return;
      }
      if (!formData.stockName) {
        showError('Validation Error', 'Stock Name is required.');
        return;
      }
      if (formData.qty === '' || isNaN(parseFloat(formData.qty))) {
        showError('Validation Error', 'Quantity is required and must be a number.');
        return;
      }
      if (!formData.referenceNumber) {
        showError('Validation Error', 'Reference Number is required.');
        return;
      }

      const dataToSubmit = {
        stock_type_id: stockTypeId,
        stone_type_id: stoneTypeId,
        karatage: karatageId,
        color: colorId,
        size: formData.stockType.toLowerCase() === 'stone' ? formData.size : null,
        weight: parseFloat(formData.weight),
        remarks: formData.remarks,
        reference_number: formData.referenceNumber,
        stock_code: formData.stockCode,
        stock_name: formData.stockName,
        qty: parseInt(formData.qty, 10),
      };

      if (selectedRawStock) {
        await dispatch(updateRawStock(selectedRawStock.id, dataToSubmit));
        showSuccess('Updated!', 'Raw Stock entry has been updated.');
      } else {
        await dispatch(createRawStock(dataToSubmit));
        showSuccess('Added!', 'Raw Stock entry has been added.');
      }
      setFormData(initialForm);
      setSelectedCode(null);
      if (onReset) onReset();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save raw stock entry.';
      showError('Error', errorMessage);
    }
  };

  const handleClear = () => {
    setFormData(initialForm);
    setSelectedCode(null);
    if (onReset) onReset();
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
        {selectedRawStock ? 'Edit Raw Stock' : 'Add Raw Stock'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <FormControl fullWidth sx={{ flex: 1 }}>
            <TextField
              name="referenceNumber"
              value={formData.referenceNumber}
              label="Reference Number"
              onChange={handleChange}
              fullWidth
              InputProps={{
                sx: { height: 56 },
                readOnly: false
              }}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ flex: 1 }}>
            <Autocomplete
              options={rawStockCodes}
              getOptionLabel={(option) => option.stock_code || ''}
              value={selectedCode}
              onChange={(_, value) => setSelectedCode(value)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stock Code (search or select)"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    sx: !!selectedCode ? { color: '#888', fontWeight: 'bold', height: 56 } : { height: 56 },
                    readOnly: false
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </FormControl>
        </Box>
        <FormControl fullWidth margin="normal" required>
          <TextField
            name="stockName"
            value={formData.stockName}
            label="Stock Name"
            onChange={handleChange}
            fullWidth
            required
            disabled={!!selectedCode}
            InputProps={{
              sx: !!selectedCode ? { color: '#888', fontWeight: 'bold' } : {},
            }}
          />
        </FormControl>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Stock Type</InputLabel>
          <Select
            name="stockType"
            value={formData.stockType}
            label="Stock Type"
            onChange={handleChange}
            disabled={!!selectedCode}
            sx={!!selectedCode ? { color: '#888', fontWeight: 'bold' } : {}}
          >
            {stocks && stocks.length > 0 ? (
              stocks.map((stock) => (
                <MenuItem key={stock.id} value={stock.name}>{stock.name}</MenuItem>
              ))
            ) : (
              <MenuItem value="">No Stock Types</MenuItem>
            )}
          </Select>
        </FormControl>
        {formData.stockType.toLowerCase() === 'gold' && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Karatage</InputLabel>
            <Select
              name="karatage"
              value={formData.karatage}
              label="Karatage"
              onChange={handleChange}
              disabled={!!selectedCode}
              sx={!!selectedCode ? { color: '#888', fontWeight: 'bold' } : {}}
            >
              {purities && purities.length > 0 ? (
                purities.map((purity) => (
                  <MenuItem key={purity.id} value={purity.name}>{purity.name}</MenuItem>
                ))
              ) : (
                <MenuItem value="">No Karatage</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
        {formData.stockType.toLowerCase() === 'gold' && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Color</InputLabel>
            <Select
              name="color"
              value={formData.color}
              label="Color"
              onChange={handleChange}
              disabled={!!selectedCode}
              sx={!!selectedCode ? { color: '#888', fontWeight: 'bold' } : {}}
            >
              {colors && colors.length > 0 ? (
                colors.map((color) => (
                  <MenuItem key={color.id} value={color.name}>{color.name}</MenuItem>
                ))
              ) : (
                <MenuItem value="">No Colors</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
        {formData.stockType.toLowerCase() === 'stone' && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Stone Type</InputLabel>
            <Select
              name="stoneType"
              value={formData.stoneType}
              label="Stone Type"
              onChange={handleChange}
              disabled={!!selectedCode}
              sx={!!selectedCode ? { color: '#888', fontWeight: 'bold' } : {}}
            >
              {stones && stones.length > 0 ? (
                stones.map((stone) => (
                  <MenuItem key={stone.id} value={stone.name}>{stone.name}</MenuItem>
                ))
              ) : (
                <MenuItem value="">No Stone Types</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth margin="normal" required>
          <TextField
            name="qty"
            value={formData.qty}
            label="Quantity"
            type="number"
            onChange={handleChange}
            fullWidth
            required
          />
        </FormControl>
        <FormControl fullWidth margin="normal" required>
          <TextField
            name="weight"
            value={formData.weight}
            label="Weight"
            type="number"
            onChange={handleChange}
            fullWidth
            required
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <TextField
            name="remarks"
            value={formData.remarks}
            label="Remarks"
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
          />
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button type="submit" variant="contained" color="primary" fullWidth>
            {selectedRawStock ? 'UPDATE' : 'ADD'}
          </Button>
          <Button type="button" variant="outlined" color="primary" fullWidth onClick={handleClear}>
            CLEAR
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default RawStockForm; 